// ABOUTME: Authentication functions backed by the Tradespurple Worker API.
// ABOUTME: Uses Bearer tokens stored in localStorage for session persistence.

import { api } from "@/lib/supabase/client";
import { User } from "./types";
import { AuthError as AppAuthError } from "./errors";
import { handleApiError } from "@/lib/errors";

type AuthResponse = { user: User; bearer?: string };
type SessionResponse = { user: User | null };

let _authListeners: Array<(user: User | null) => void> = [];

function notifyListeners(user: User | null) {
  for (const fn of _authListeners) fn(user);
}

export async function signUp(email: string, password: string) {
  try {
    const data = await api.post<AuthResponse>('/auth/signup', { email, password });
    if (data.bearer) api.setToken(data.bearer);
    notifyListeners(data.user);
    return { session: { user: data.user }, user: data.user };
  } catch (error) {
    if (error instanceof Error) throw new AppAuthError(error.message, { email });
    throw error;
  }
}

export async function signIn(email: string, password: string) {
  try {
    const data = await api.post<AuthResponse>('/auth/signin', { email, password });
    if (data.bearer) api.setToken(data.bearer);
    notifyListeners(data.user);
    return { session: { user: data.user }, user: data.user };
  } catch (error) {
    if (error instanceof Error) throw new AppAuthError(error.message, { email });
    throw error;
  }
}

export async function signOut() {
  try {
    await api.post('/auth/signout');
    api.setToken(null);
    notifyListeners(null);
  } catch (error) {
    handleApiError(error, 'Failed to sign out. Please try again.');
    throw error;
  }
}

export async function getSession() {
  try {
    const data = await api.get<SessionResponse>('/auth/session');
    return { data: { session: data.user ? { user: data.user } : null }, error: null };
  } catch {
    return { data: { session: null }, error: null };
  }
}

export async function ensureFreshAuth() {
  const { data } = await getSession();
  if (!data.session) throw new Error('No active session found');
  return data.session;
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post('/auth/request-password-reset', { email });
}

export async function resetPassword(token: string, password: string) {
  const data = await api.post<AuthResponse>('/auth/reset-password', { token, password });
  if (data.bearer) api.setToken(data.bearer);
  notifyListeners(data.user);
  return { user: data.user };
}

export function onAuthStateChange(callback: (event: string, session: { user: User } | null) => void) {
  const listener = (user: User | null) => {
    callback(user ? 'SIGNED_IN' : 'SIGNED_OUT', user ? { user } : null);
  };
  _authListeners.push(listener);
  return {
    data: {
      subscription: {
        unsubscribe: () => {
          _authListeners = _authListeners.filter(fn => fn !== listener);
        },
      },
    },
  };
}
